/**
 * Tool confirmation component for user interactions during agentic flows
 * Based on gemini-cli ToolConfirmationMessage component
 */

import React, { useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  ToolCallConfirmationDetails, 
  ToolConfirmationOutcome,
  ToolEditConfirmationDetails,
  ToolExecuteConfirmationDetails,
  ToolInfoConfirmationDetails,
  ToolMcpConfirmationDetails
} from '../../../../shared-types/dist/index.js';
import { AlertTriangle, Play, Info, Server } from 'lucide-react';

interface ToolConfirmationBubbleProps {
  confirmationDetails: ToolCallConfirmationDetails;
  onConfirm: (outcome: ToolConfirmationOutcome) => void;
  className?: string;
}

export const ToolConfirmationBubble: React.FC<ToolConfirmationBubbleProps> = ({
  confirmationDetails,
  onConfirm,
  className = ''
}) => {
  const handleConfirm = useCallback((outcome: ToolConfirmationOutcome) => {
    onConfirm(outcome);
  }, [onConfirm]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleConfirm(ToolConfirmationOutcome.Cancel);
    } else if (event.key === 'Enter' && !event.shiftKey) {
      handleConfirm(ToolConfirmationOutcome.ProceedOnce);
    }
  }, [handleConfirm]);

  const renderContent = () => {
    switch (confirmationDetails.type) {
      case 'edit':
        return renderEditConfirmation(confirmationDetails as ToolEditConfirmationDetails);
      case 'exec':
        return renderExecConfirmation(confirmationDetails as ToolExecuteConfirmationDetails);
      case 'info':
        return renderInfoConfirmation(confirmationDetails as ToolInfoConfirmationDetails);
      case 'mcp':
        return renderMcpConfirmation(confirmationDetails as ToolMcpConfirmationDetails);
      default:
        return null;
    }
  };

  const renderEditConfirmation = (details: ToolEditConfirmationDetails) => {
    if (details.isModifying) {
      return (
        <div className="flex items-center space-x-2 text-green-600">
          <AlertTriangle className="h-4 w-4" />
          <span>Modify in progress: Save and close external editor to continue</span>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="border rounded p-3 bg-gray-50 max-h-64 overflow-auto">
          <div className="text-xs text-gray-500 mb-2 font-medium">File: {details.fileName}</div>
          <pre className="font-mono text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere">{details.fileDiff}</pre>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedOnce)}
            variant="default"
            size="sm"
          >
            Yes, allow once
          </Button>
          <Button 
            onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedAlways)}
            variant="secondary"
            size="sm"
          >
            Yes, allow always
          </Button>
          <Button 
            onClick={() => handleConfirm(ToolConfirmationOutcome.ModifyWithEditor)}
            variant="outline"
            size="sm"
          >
            Modify with external editor
          </Button>
          <Button 
            onClick={() => handleConfirm(ToolConfirmationOutcome.Cancel)}
            variant="destructive"
            size="sm"
          >
            No (esc)
          </Button>
        </div>
      </div>
    );
  };

  const renderExecConfirmation = (details: ToolExecuteConfirmationDetails) => (
    <div className="space-y-4">
      <div className="border rounded p-3 bg-accent">
        <code className="font-mono text-sm text-primary break-words overflow-wrap-anywhere">{details.command}</code>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedOnce)}
          variant="default"
          size="sm"
        >
          Yes, allow once
        </Button>
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedAlways)}
          variant="secondary"
          size="sm"
          title={`Always allow "${details.rootCommand} ..."`}
        >
          Yes, allow always "{details.rootCommand} ..."
        </Button>
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.Cancel)}
          variant="destructive"
          size="sm"
        >
          No (esc)
        </Button>
      </div>
    </div>
  );

  const renderInfoConfirmation = (details: ToolInfoConfirmationDetails) => (
    <div className="space-y-4">
      <div className="border rounded p-3 bg-accent">
        <div className="text-primary font-medium">{details.prompt}</div>
        {details.urls && details.urls.length > 0 && 
         (details.urls.length > 1 || details.urls[0] !== details.prompt) && (
          <div className="mt-2">
            <div className="text-sm text-gray-600">URLs to fetch:</div>
            <ul className="list-disc list-inside text-sm space-y-1">
              {details.urls?.map((url: string, index: number) => (
                <li key={index} className="text-primary">{url}</li>
              )) || []}
            </ul>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedOnce)}
          variant="default"
          size="sm"
        >
          Yes, allow once
        </Button>
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedAlways)}
          variant="secondary"
          size="sm"
        >
          Yes, allow always
        </Button>
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.Cancel)}
          variant="destructive"
          size="sm"
        >
          No (esc)
        </Button>
      </div>
    </div>
  );

  const renderMcpConfirmation = (details: ToolMcpConfirmationDetails) => (
    <div className="space-y-4">
      <div className="border rounded p-3 bg-accent">
        <div className="flex items-center space-x-2 text-primary">
          <Server className="h-4 w-4" />
          <span className="font-medium">MCP Server: {details.serverName}</span>
        </div>
        <div className="text-primary mt-1">Tool: {details.toolName}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedOnce)}
          variant="default"
          size="sm"
        >
          Yes, allow once
        </Button>
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedAlwaysTool)}
          variant="secondary"
          size="sm"
          title={`Always allow tool "${details.toolName}" from server "${details.serverName}"`}
        >
          Yes, always allow tool "{details.toolName}"
        </Button>
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.ProceedAlwaysServer)}
          variant="outline"
          size="sm"
          title={`Always allow all tools from server "${details.serverName}"`}
        >
          Yes, always allow all tools from "{details.serverName}"
        </Button>
        <Button 
          onClick={() => handleConfirm(ToolConfirmationOutcome.Cancel)}
          variant="destructive"
          size="sm"
        >
          No (esc)
        </Button>
      </div>
    </div>
  );

  const getIcon = () => {
    switch (confirmationDetails.type) {
      case 'edit': return <AlertTriangle className="h-4 w-4" />;
      case 'exec': return <Play className="h-4 w-4" />;
      case 'info': return <Info className="h-4 w-4" />;
      case 'mcp': return <Server className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getTitle = () => {
    switch (confirmationDetails.type) {
      case 'edit': return 'Apply this change?';
      case 'exec': return 'Allow execution?';
      case 'info': return 'Do you want to proceed?';
      case 'mcp': return `Allow execution of MCP tool "${(confirmationDetails as ToolMcpConfirmationDetails).toolName}"?`;
      default: return 'Confirmation required';
    }
  };

  return (
    <Card 
      className={`border-l-4 border-l-yellow-400 bg-yellow-50 w-full max-w-none ${className}`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center space-x-2 text-sm font-medium">
          {getIcon()}
          <span>{getTitle()}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {renderContent()}
        <div className="mt-3 text-xs text-gray-500">
          Tip: Press Enter for "allow once", Esc to cancel
        </div>
      </CardContent>
    </Card>
  );
};